import React, { useState, useRef, useEffect } from "react";
import { publicApi } from "../api";
import CameraCapture from "./CameraCapture";
import normalizeImage from "../utils/normalizeImage";

/**
 * Driver-licence capture step (FOXID). The two tiles (Front / Back) are simple
 * status targets; tapping either opens ONE large, shared camera modal
 * (CameraCapture, reused for both sides) with a live preview, a card-frame
 * guide and a 3-second self-timer. Every path falls back to a file upload, and
 * manual entry is always available. The optional selfie / face-match step is a
 * separate step after this one (see StepSelfie).
 */

const CARD_ASPECT = "1.586 / 1";

/* ── Status target tile: empty → captured thumbnail. No embedded camera. ── */
function LicenceTarget({ label, hint, image, onOpen, onUpload }) {
  const fileRef = useRef(null);
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [image]);
  // Upload = pick an existing file. No `capture` attr (it forces the camera on
  // mobile); normalise to a downscaled JPEG so big/rotated uploads reach FOXID.
  const onFile = async (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUpload(await normalizeImage(f)); };
  const hiddenFile = <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={onFile} />;

  if (image) {
    return (
      <div className="su-licence-tile is-filled">
        <div className="su-licence-frame" style={{ background: "#0f172a" }}>
          {broken ? (
            <div className="su-licence-broken">That photo didn't come through.<br />Retake or upload below.</div>
          ) : (
            <img src={image} alt={label} onError={() => setBroken(true)} />
          )}
          <span className="su-licence-badge">{label}</span>
        </div>
        <div className="su-licence-links">
          <button type="button" className="su-link-btn" onClick={onOpen}>
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></svg>
            Retake
          </button>
          <button type="button" className="su-link-btn" onClick={() => fileRef.current?.click()}>Upload</button>
        </div>
        {hiddenFile}
      </div>
    );
  }

  return (
    <div className="su-licence-tile">
      <button type="button" className="su-licence-frame su-licence-empty" onClick={onOpen}>
        <svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" />
        </svg>
        <span className="su-licence-label">{label}</span>
        <span className="su-licence-hint">{hint}</span>
        <span className="su-licence-cta">Tap to open camera</span>
      </button>
      <div className="su-licence-links">
        <button type="button" className="su-link-btn" onClick={() => fileRef.current?.click()}>or upload a photo</button>
      </div>
      {hiddenFile}
    </div>
  );
}

/* ── Step ── */
export default function StepLicence({ slug, venue, isExisting, onScanned, onSkip, onBack }) {
  const [front, setFront] = useState(null); // { blob, url }
  const [back, setBack] = useState(null);
  const [camSide, setCamSide] = useState(null); // "Front" | "Back" | null
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState("");
  const cancelledRef = useRef(false);

  // Mirror the live images so unmount cleanup revokes what's current without
  // firing on every change (which would revoke the other side's URL).
  const frontRef = useRef(null);
  const backRef = useRef(null);
  useEffect(() => { frontRef.current = front; }, [front]);
  useEffect(() => { backRef.current = back; }, [back]);
  useEffect(() => () => {
    if (frontRef.current?.url) URL.revokeObjectURL(frontRef.current.url);
    if (backRef.current?.url) URL.revokeObjectURL(backRef.current.url);
  }, []);

  const setImage = (side, blob) => {
    const url = URL.createObjectURL(blob);
    setError("");
    if (side === "Front") { if (front?.url) URL.revokeObjectURL(front.url); setFront({ blob, url }); }
    else { if (back?.url) URL.revokeObjectURL(back.url); setBack({ blob, url }); }
    setCamSide(null);
  };
  const submit = async () => {
    if (!front) { setError("Please capture the front of your licence."); return; }
    setCamSide(null);
    setScanning(true); setError("");
    try {
      const res = await publicApi.foxidScan(slug, {
        front: front.blob,
        back: back?.blob || null,
        source: isExisting ? "renewal" : "signup",
      });
      if (cancelledRef.current) return;
      onScanned(res.fields || {}, res.scanRef || null);
    } catch (err) {
      if (cancelledRef.current) return;
      setError(err.message || "We couldn't read your licence. Please retake the photos, or enter your details manually.");
    } finally {
      if (!cancelledRef.current) setScanning(false);
    }
  };

  const skip = () => { cancelledRef.current = true; onSkip(); };

  return (
    <>
      <h2 className="step-title">Scan your licence</h2>
      <p className="step-sub">
        Photograph your driver licence and we'll fill in your details for you — no typing. Tap a card below to open
        the camera, line the licence up inside the frame, and a 3-second timer takes the shot.
      </p>

      {error && <div className="su-alert error" style={{ marginBottom: "1rem" }}>⚠ {error}</div>}

      <div className="su-licence-grid">
        <LicenceTarget
          label="Front" hint="Required" image={front?.url}
          onOpen={() => { setError(""); setCamSide("Front"); }}
          onUpload={(b) => setImage("Front", b)}
        />
        <LicenceTarget
          label="Back" hint="Recommended" image={back?.url}
          onOpen={() => { setError(""); setCamSide("Back"); }}
          onUpload={(b) => setImage("Back", b)}
        />
      </div>
      <p className="step-sub" style={{ fontSize: "0.78rem", marginTop: "0.75rem" }}>
        The back of the card carries extra details (like the card number) — adding it gives the best result.
      </p>

      <div className="su-btn-row" style={{ marginTop: "1.25rem" }}>
        <button type="button" className="su-btn su-btn-outline" onClick={onBack} disabled={scanning}>Back</button>
        <button type="button" className="su-btn su-btn-primary" onClick={submit} disabled={scanning || !front}>
          {scanning ? "Reading licence…" : "Scan licence"}
        </button>
      </div>

      <div style={{ textAlign: "center", marginTop: "1rem" }}>
        <button type="button" onClick={skip}
          style={{ background: "none", border: "none", color: "var(--su-text, #475569)", opacity: 0.8, fontSize: "0.85rem", textDecoration: "underline", cursor: "pointer" }}>
          I'll enter my details manually
        </button>
      </div>

      {camSide && (
        <CameraCapture
          variant="card"
          title={`${camSide} of licence`}
          hint="Line the whole card up inside the frame — in focus and glare-free."
          onCapture={(blob) => setImage(camSide, blob)}
          onClose={() => setCamSide(null)}
        />
      )}
    </>
  );
}

export { CARD_ASPECT };
